import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePDF } from "react-to-pdf";
import {
    Box, Button, Chip, Dialog, DialogContent, Divider, Grid,
    IconButton, Paper, Stack, Tooltip, Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import Application from "../../../components/application";
import MyPanel from "../../../components/myPanel";
import MyButton from "../../../components/myButton";
import MyLoading from "../../../components/myLoading";
import useTarefaFormularioState from "../../../hooks/useTarefaFromulario";
import MyMapModal from "../../../components/myMapModal";
import { AuthContext } from "../../../contexts/auth";
import TELAS from "../../../constants/telas";
import TELA_ACOES from "../../../constants/telaAcoes";
import permissaoTelas from "../../../utils/permissaoTelas";

const PAGINA = {
    aplicacao: "SisPalo - Tarefas",
    funcionalidade: "Tarefas",
    tela: TELAS.TAREFAS,
    acoes: [TELA_ACOES.Consultar, TELA_ACOES.Geolocalização]
};

const Info = ({ title, value, children, destaque = false }) => (
    <Box sx={{
        minWidth: 0,
        ...(destaque && {
            height: "100%", py: 1, px: 1.5,
            border: "1px solid", borderColor: "divider", borderRadius: 1.5,
            transition: "border-color .2s ease, box-shadow .2s ease",
            "&:hover": { borderColor: "primary.light", boxShadow: "0 3px 12px rgba(0,0,0,.06)" }
        })
    }}>
        <Typography
            variant={destaque ? "body2" : "caption"}
            color={destaque ? "primary.main" : "text.secondary"}
            sx={{ display: "block", mb: destaque ? 0.4 : 0.2, fontWeight: destaque ? 700 : 400, letterSpacing: destaque ? 0.1 : 0 }}
        >
            {title}
        </Typography>
        {children || (
            <Typography variant="body2" sx={{ fontWeight: destaque ? 400 : 600, color: destaque ? "text.primary" : "inherit", lineHeight: 1.4, overflowWrap: "anywhere" }}>
                {value || "—"}
            </Typography>
        )}
    </Box>
);

const Resposta = ({ pergunta, resposta, tipo, ampliarImagem }) => {
    const valor = tipo === 1 ? (resposta === "true" ? "Sim" : "Não") : resposta;

    if (tipo === 3 || tipo === 4) {
        return (
            <Box sx={{
                py: 1, px: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.5,
                transition: "border-color .2s ease, box-shadow .2s ease",
                "&:hover": { borderColor: "primary.light", boxShadow: "0 3px 12px rgba(0,0,0,.06)" }
            }}>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700, mb: 0.75, letterSpacing: .1 }}>{pergunta}</Typography>
                <Box
                    component="button"
                    type="button"
                    onClick={() => ampliarImagem({ src: resposta, titulo: pergunta })}
                    aria-label={`Ampliar imagem: ${pergunta}`}
                    sx={{
                        position: "relative", display: "block", p: 0, border: "1px solid",
                        borderColor: "divider", borderRadius: 1.5, overflow: "hidden",
                        bgcolor: "grey.100", cursor: "zoom-in", maxWidth: 280,
                        "&:hover img": { transform: "scale(1.03)" },
                        "&:hover .zoom": { opacity: 1 }
                    }}
                >
                    <Box component="img" src={resposta} alt={pergunta} sx={{
                        display: "block", width: "100%", height: 145, objectFit: "contain",
                        transition: "transform .2s ease"
                    }} />
                    <Box className="zoom" sx={{
                        position: "absolute", right: 8, bottom: 8, display: "flex", p: 0.75,
                        borderRadius: "50%", bgcolor: "rgba(0,0,0,.65)", color: "white", opacity: .8
                    }}><ZoomInIcon fontSize="small" /></Box>
                </Box>
                <Typography variant="caption" color="text.secondary">Clique para ampliar</Typography>
            </Box>
        );
    }

    return <Info title={pergunta} value={valor} destaque />;
};

const TarefasFormulario = () => {
    const { id } = useParams();
    const states = useTarefaFormularioState();
    const [imprimindo, setImprimindo] = useState(false);
    const [mapa, setMapa] = useState({ open: false, latitude: null, longitude: null });
    const [imagem, setImagem] = useState(null);
    const [zoomImagem, setZoomImagem] = useState(1);
    const [arrastandoImagem, setArrastandoImagem] = useState(false);
    const areaImagemRef = useRef(null);
    const inicioArrasteRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tipoTarefa = searchParams.get("tipotarefa") || undefined;
    const { permissaoUsuario } = useContext(AuthContext);
    const { toPDF, targetRef } = usePDF({ filename: `tarefa_${id}.pdf` });
    const origem = searchParams.get("origem");
    const rotaVoltar = origem === "veiculos"
        ? "/gestor/checklist-veiculos"
        : origem === "dashboard-tarefas"
            ? "/gestor/dashboard-tarefas"
            : "/gestor/tarefas";

    useEffect(() => {
        if (permissaoTelas(permissaoUsuario, TELA_ACOES.Consultar, PAGINA)) {
            if (id) states.tarefaFormulario.set(id, tipoTarefa);
        } else {
            navigate("/gestor/dashboard");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (imprimindo) {
            toPDF().finally(() => setImprimindo(false));
        }
    }, [imprimindo, toPDF]);

    const tarefa = states.tarefa.value;
    const detalhes = states.tarefaFormulario.value[0];
    const secoes = detalhes?.secoes?.slice().sort((a, b) => a.ordem - b.ordem) || [];
    const temRespostasFormulario = secoes.some((secao) =>
        secao.perguntas?.some((pergunta) => Boolean(pergunta.resposta))
    );
    const abrirImagem = (novaImagem) => {
        setZoomImagem(1);
        setImagem(novaImagem);
    };
    const fecharImagem = () => {
        setImagem(null);
        setZoomImagem(1);
    };
    const alterarZoom = (event) => {
        event.preventDefault();
        const direcao = event.deltaY < 0 ? 0.15 : -0.15;
        setZoomImagem((zoomAtual) => Math.min(4, Math.max(0.5, Number((zoomAtual + direcao).toFixed(2)))));
    };
    const iniciarArraste = (event) => {
        if (zoomImagem <= 1 || !areaImagemRef.current) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        inicioArrasteRef.current = {
            x: event.clientX,
            y: event.clientY,
            scrollLeft: areaImagemRef.current.scrollLeft,
            scrollTop: areaImagemRef.current.scrollTop
        };
        setArrastandoImagem(true);
    };
    const moverImagem = (event) => {
        if (!arrastandoImagem || !areaImagemRef.current) return;
        const inicio = inicioArrasteRef.current;
        areaImagemRef.current.scrollLeft = inicio.scrollLeft - (event.clientX - inicio.x);
        areaImagemRef.current.scrollTop = inicio.scrollTop - (event.clientY - inicio.y);
    };
    const finalizarArraste = (event) => {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setArrastandoImagem(false);
    };

    return (
        <Application>
            <MyPanel>
                <MyLoading render={states.submit.value || imprimindo} />
                <Stack spacing={1.5} ref={targetRef} sx={{ p: { xs: 0.25, sm: 1 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                        <Box>
                            <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>Detalhes da tarefa</Typography>
                            <Typography variant="body2" color="text.secondary">Consulte os dados e as respostas registradas.</Typography>
                        </Box>
                        {!imprimindo && <MyButton color="warning" onClick={() => navigate(rotaVoltar)} variant="contained" text="Voltar" />}
                    </Box>

                    <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
                        <Box sx={{ px: 1.5, py: 1, bgcolor: "primary.main", color: "primary.contrastText", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{String(tarefa?.tipotarefa_nome || "").trim() || "Check-in"}</Typography>
                            <Chip label={tarefa?.saida ? "Finalizada" : "Em andamento"} size="small" sx={{ bgcolor: tarefa?.saida ? "success.light" : "warning.light", fontWeight: 700 }} />
                        </Box>
                        <Grid container spacing={1.25} sx={{ p: 1.5 }}>
                            <Grid item xs={12} sm={6} md={3}><Info title="Local" value={tarefa?.local_name} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><Info title="Posto" value={tarefa?.posto} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><Info title="Usuário" value={tarefa?.usuario_nome} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><Info title="Entrada" value={tarefa?.entrada} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><Info title="Saída" value={tarefa?.saida} /></Grid>
                            <Grid item xs={12} sm={6} md={5}>
                                <Info title="GPS registrado">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tarefa?.latitude && tarefa?.longitude ? `${tarefa.latitude}, ${tarefa.longitude}` : "—"}</Typography>
                                        <Tooltip title="Ver localização no mapa" arrow>
                                            <span>
                                                <Button size="small" onClick={() => setMapa({ open: true, latitude: tarefa?.latitude, longitude: tarefa?.longitude })}
                                                    disabled={!tarefa?.saida || !permissaoTelas(permissaoUsuario, TELA_ACOES.Geolocalização, PAGINA)}
                                                    startIcon={<FmdGoodIcon />} sx={{ minWidth: 0 }}>Mapa</Button>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Info>
                            </Grid>
                            {tarefa?.descricao && <Grid item xs={12}><Divider sx={{ mb: 1 }} /><Info title="Descrição" value={tarefa.descricao} /></Grid>}
                        </Grid>
                    </Paper>

                    {temRespostasFormulario && <Typography variant="h6" sx={{ fontWeight: 700 }}>Respostas do formulário</Typography>}
                    {temRespostasFormulario && secoes.map((secao, secaoIndex) => {
                        const perguntas = secao.perguntas?.slice().sort((a, b) => a.ordem - b.ordem).filter(p => p.resposta) || [];
                        if (!perguntas.length) return null;
                        return (
                            <Paper key={secaoIndex} variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 2 }}>
                                {secao.titulo && <><Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}>{secao.titulo}</Typography><Divider sx={{ mt: 0.5, mb: 1, borderColor: "primary.main", borderBottomWidth: 2, width: 40 }} /></>}
                                <Grid container spacing={0.75}>
                                    {perguntas.map((pergunta, index) => (
                                        <Grid item xs={12} key={index}>
                                            <Resposta pergunta={pergunta.descricao} resposta={pergunta.resposta} tipo={pergunta.tipo_resposta} ampliarImagem={abrirImagem} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        );
                    })}
                </Stack>
            </MyPanel>

            <MyMapModal mapa={{ set: setMapa, value: mapa }} />
            <Dialog open={Boolean(imagem)} onClose={fecharImagem} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: "grey.900" } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1, color: "white" }}>
                    <Box>
                        <Typography sx={{ fontWeight: 600 }}>{imagem?.titulo}</Typography>
                        <Typography variant="caption" sx={{ color: "grey.400" }}>Use o scroll para ampliar e arraste para mover • {Math.round(zoomImagem * 100)}%</Typography>
                    </Box>
                    <IconButton onClick={fecharImagem} aria-label="Fechar imagem" sx={{ color: "white" }}><CloseIcon /></IconButton>
                </Box>
                <DialogContent
                    ref={areaImagemRef}
                    onWheel={alterarZoom}
                    onPointerDown={iniciarArraste}
                    onPointerMove={moverImagem}
                    onPointerUp={finalizarArraste}
                    onPointerCancel={finalizarArraste}
                    sx={{
                        p: 1, height: "78vh", overflow: "auto", textAlign: "center",
                        cursor: zoomImagem > 1 ? (arrastandoImagem ? "grabbing" : "grab") : "zoom-in",
                        userSelect: "none", touchAction: zoomImagem > 1 ? "none" : "auto"
                    }}
                >
                    {imagem && <Box component="img" src={imagem.src} alt={imagem.titulo} sx={{
                        display: "inline-block",
                        width: zoomImagem === 1 ? "auto" : `${zoomImagem * 100}%`,
                        maxWidth: zoomImagem === 1 ? "100%" : "none",
                        maxHeight: zoomImagem === 1 ? "75vh" : "none",
                        height: "auto", objectFit: "contain", transition: arrastandoImagem ? "none" : "width .12s ease",
                        pointerEvents: "none"
                    }} draggable={false} />}
                </DialogContent>
            </Dialog>
        </Application>
    );
};

export default TarefasFormulario;
